import { AxisBottom, AxisLeft } from '@visx/axis';
import { curveMonotoneX } from '@visx/curve';
import { GridRows } from '@visx/grid';
import { scaleLinear, scaleTime } from '@visx/scale';
import { AreaClosed, LinePath } from '@visx/shape';
import { Tooltip } from 'antd';
import { extent, max } from 'd3-array';
import { timeFormat } from 'd3-time-format';
import React, { useMemo } from 'react';
import * as allCurves from '@visx/curve';
import { MeetingBotVMHealth } from '../../../models/meeting-bot-vm-interface';

// accessors
const getDate = (d: MeetingBotVMHealth) => new Date(d.recordDate + "Z");
const getCount = (d: MeetingBotVMHealth) => d.cpuPercentage;

const formatAxisDate = timeFormat('%X');

export type CPUUsageChartProps = {
  width: number;
  height: number;
  data: MeetingBotVMHealth[] | undefined;
  isLoading: boolean;
  isFullDetail?: boolean;
};

export default function CPUUsageChart({
  width,
  height,
  data = [],
  isLoading,
  isFullDetail
}: CPUUsageChartProps) {
  // bounds
  const innerWidth = width - 80;
  const innerHeight = height === 0 ? 0 : height - 50 - (width > 350 ? 30 : 0);

  // scales
  const dateScale = useMemo(
    () =>
      scaleTime({
        range: [50, innerWidth + 50],
        domain: extent(data, getDate) as [Date, Date],
        nice: true,
      }),
    [data, innerWidth]
  );
  const countValueScale = useMemo(
    () =>
      scaleLinear({
        range: [innerHeight, 0],
        domain: [0, (max(data, getCount) || 0) + 10],
        nice: true,
      }),
    [innerHeight, data]
  );

  if (width < 10) return null;

  return (
    <svg
      width={width}
      height={height > 50 ? height - 50 : 0}
      style={{ backgroundColor: '#fff' }}
    >
      {data.filter((d, i) => !isFullDetail || (isFullDetail && ((i == 0 || i == data.length - 1) || (Math.round(d.cpuPercentage) != Math.round(data[i - 1].cpuPercentage) && Math.round(d.cpuPercentage) != Math.round(data[i + 1].cpuPercentage))))).map((d, j) => (
        <Tooltip
          key={j}
          title={
            <div>
              <b>
                {new Date(d.recordDate + "Z").toLocaleTimeString('en-us')}
              </b>
              <br />
              <div>{`CPU Percentage: ${d.cpuPercentage}`}</div>
            </div>
          }
          color='#6c0cc6'
        >
          <circle
            className='informational-text'
            key={j}
            r={4}
            cx={dateScale(getDate(d))}
            cy={countValueScale(getCount(d))}
            stroke={isLoading ? '#767878' : '#6c0cc6'}
            fill={isLoading ? '#767878' : '#6c0cc6'}
          />
        </Tooltip>
      ))}
      <LinePath<MeetingBotVMHealth>
        curve={allCurves['curveLinear']}
        data={data}
        x={(d) => dateScale(getDate(d)) ?? 0}
        y={(d) => countValueScale(getCount(d)) ?? 0}
        stroke={isLoading ? '#767878' : '#6c0cc6'}
        strokeWidth={2}
        strokeOpacity={1}
        shapeRendering='geometricPrecision'
      />
      <GridRows
        left={50}
        scale={countValueScale}
        width={innerWidth}
        strokeDasharray='1,3'
        stroke={'#000000'}
        strokeOpacity={0.5}
        pointerEvents='none'
        numTicks={5}
      />
      <AreaClosed<MeetingBotVMHealth>
        data={data}
        x={(d) => dateScale(getDate(d)) ?? 0}
        y={(d) => countValueScale(getCount(d)) ?? 0}
        yScale={countValueScale}
        strokeWidth={1}
        stroke='url(#area-gradient)'
        fill='url(#area-gradient)'
        curve={curveMonotoneX}
      />
      <AxisBottom
        top={innerHeight}
        scale={dateScale}
        tickFormat={formatAxisDate}
        tickLabelProps={() => ({
          fill: '#000000',
          fontSize: 12,
          textAnchor: 'middle',
        })}
        numTicks={7}
        hideAxisLine
        hideTicks
      />
      <AxisLeft
        left={20}
        scale={countValueScale}
        tickLabelProps={() => ({
          fill: '#000000',
          fontSize: 12,
        })}
        hideAxisLine
        hideTicks
        numTicks={5}
      />
    </svg>
  );
}
